/**
 * 游戏外壳 ↔ 模拟内核的唯一接口（G008A）。
 *
 * 边界约定：
 * - **内核持有权威状态**（资源、建筑、道路、时钟）。本模块只做命令翻译与只读投影，
 *   自己不存任何领域状态 —— 一旦这里出现可变账本，接线就白做了。
 * - main.ts 只与本模块对话，不直接 import simulation-core，也不直接改 worldState。
 * - 网格常量以内核 SpatialMapConfig 为唯一来源；渲染用的 cellCenter 也从内核取，
 *   两套坐标不可能再各算各的。
 */
import {
  SimulationKernel,
  createPlanBuildingHandler,
  createSeedBuildingHandler,
  createLayRoadHandler,
  createCancelBuildingHandler,
  createDeconstructBuildingHandler,
  createMarkDamagedHandler,
  createSpendResourcesHandler,
  createLayCableHandler,
  createRemoveCableHandler,
  createSeedCableHandler,
  createPowerSystem,
  createLayPipeHandler,
  createRemovePipeHandler,
  createSeedPipeHandler,
  createSetValveHandler,
  createFluidSystem,
  createProductionSystem,
  createPopulationSystem,
  createAssignJobHandler,
  createUnassignJobHandler,
  createSetShiftHandler,
  autoAssign,
  seedPopulation,
  citizenList,
  jobPanel,
  populationSummary,
  staffOf,
  jobOf,
  type JobSpec,
  type ShiftId,
  type SkillId,
  SeededRng,
  productionStatusOf,
  economySummary,
  type ProductionSpec,
  type ProductionBlock,
  type UtilityProbe,
  buildEnvelope,
  parseEnvelope,
  type SaveEnvelope,
  type ViewMeta,
  fluidSummary as fluidSummaryOf,
  fluidStatusOf,
  type FluidSpec,
  type FluidState,
  powerSummary as powerSummaryOf,
  powerStatusOf,
  powerTopology,
  type PowerSpec,
  type PowerState,
  createLogisticsSystem,
  createConstructionWorkSystem,
  listBuildings,
  cellToWorld,
  cellKey as simCellKey,
  sectorsOfRing,
  getCell,
  resourceShortages,
  constructionOutlook,
  type PlanBuildingSpec,
  type BuildCellId,
  type SpatialMapConfig,
  type BuildingComponent,
} from '@crater-zero/simulation-core';
import type { CommandId, GameCommand } from '@crater-zero/shared-types';

/* ============ 网格配置：与 main.ts 的视觉常量逐值对齐 ============ */
export const RING_R0 = 96;      // ring 0 中心半径
export const RING_STEP = 46;    // 环间距
export const RING_COUNT = 7;
export const CELL_PITCH = 49;   // 目标切向弧长（略大于最宽建筑 42）

const MAP_CONFIG: SpatialMapConfig = {
  sectorCount: 96,                    // 上限；实际每环由 arcPitch 推导
  ringsPerLayer: { floor: RING_COUNT, wall: 4, subsurface: 4 },
  floorRadius: 384,
  ringWidth: RING_STEP,
  wallBandHeight: 40,
  floorBaseY: 0,
  arcPitch: CELL_PITCH,
  ringRadius0: RING_R0,
};

/* ============ 建筑定义：来自 content/，不在代码里硬编码 ============ */
interface RawBuildingJson {
  id: string;
  allowedLayers: string[];
  footprint: { radial: number; angular: number; rotationSteps: number };
  construction: { workMinutes: number; cost: Record<string, number>; refundBasisPoints: number };
  placement?: { maxSlopeBasisPoints?: number; minSupport?: number; maxContamination?: number };
  /** G011：配方、岗位、维护、仓储与缓冲容量。 */
  operation?: {
    workerSlots?: number;
    /** G012：岗位技能要求（技能 id -> 等级 0..100）。 */
    requiredSkills?: Record<string, number>;
    recipes?: Array<{
      id: string; cycleMinutes: number;
      inputs?: Record<string, number>; outputs?: Record<string, number>;
      requiresWater?: number;
    }>;
    maintenancePerDay?: Record<string, number>;
    storageCapacity?: number;
    bufferCapacity?: number;
  };
  utility?: {
    powerOutputWatts?: number; powerDrawWatts?: number;
    storagePowerWatts?: number; storageCapacityWattMinutes?: number;
    sheddingPriority?: number;
    oxygenPerMinute?: number; oxygenDrawPerMinute?: number; oxygenCapacity?: number;
    rawWaterPerMinute?: number; waterDrawPerMinute?: number; waterCapacity?: number;
    purifyPerMinute?: number; contaminationPerMinute?: number;
    productionRequiresPower?: boolean;
  };
}
const DEF_MODULES = import.meta.glob<RawBuildingJson>(
  '../../../content/samples/buildings/*.json',
  { eager: true, import: 'default' },
);
const DEFS: Record<string, PlanBuildingSpec> = {};
for (const raw of Object.values(DEF_MODULES)) {
  DEFS[raw.id] = {
    id: raw.id,
    allowedLayers: raw.allowedLayers as PlanBuildingSpec['allowedLayers'],
    footprint: { radial: raw.footprint.radial, angular: raw.footprint.angular },
    rotationSteps: raw.footprint.rotationSteps,
    cost: raw.construction.cost,
    workMinutes: raw.construction.workMinutes,
    refundBasisPoints: raw.construction.refundBasisPoints,
    placement: raw.placement ?? {},
  };
}
const getDef = (id: string): PlanBuildingSpec | undefined => DEFS[id];

/** 建筑定义 -> 电力投影。缺省一律为 0，即"不参与电网"。 */
const POWER_SPECS: Record<string, PowerSpec> = {};
for (const raw of Object.values(DEF_MODULES)) {
  const u = raw.utility;
  if (!u) continue;
  POWER_SPECS[raw.id] = {
    id: raw.id,
    outputWatts: u.powerOutputWatts ?? 0,
    drawWatts: u.powerDrawWatts ?? 0,
    storagePowerWatts: u.storagePowerWatts ?? 0,
    storageCapacityWattMinutes: u.storageCapacityWattMinutes ?? 0,
    sheddingPriority: u.sheddingPriority ?? 50,
  };
}
const getPowerSpec = (id: string): PowerSpec | undefined => POWER_SPECS[id];

/** 建筑定义 -> 流体投影。 */
const FLUID_SPECS: Record<string, FluidSpec> = {};
for (const raw of Object.values(DEF_MODULES)) {
  const u = raw.utility;
  if (!u) continue;
  FLUID_SPECS[raw.id] = {
    id: raw.id,
    production: { oxygen: u.oxygenPerMinute ?? 0, water_raw: u.rawWaterPerMinute ?? 0 },
    demand: { oxygen: u.oxygenDrawPerMinute ?? 0, water_clean: u.waterDrawPerMinute ?? 0 },
    capacity: { oxygen: u.oxygenCapacity ?? 0, water_clean: u.waterCapacity ?? 0 },
    purifyPerMinute: u.purifyPerMinute ?? 0,
    contaminationPerMinute: u.contaminationPerMinute ?? 0,
    requiresPower: u.productionRequiresPower ?? false,
  };
}
const getFluidSpec = (id: string): FluidSpec | undefined => FLUID_SPECS[id];

/** 建筑定义 -> 生产投影（G011）。缺省一律「不生产」。 */
const PRODUCTION_SPECS: Record<string, ProductionSpec> = {};
for (const raw of Object.values(DEF_MODULES)) {
  const op = raw.operation;
  if (!op) continue;
  PRODUCTION_SPECS[raw.id] = {
    id: raw.id,
    recipes: (op.recipes ?? []).map((r) => ({
      id: r.id,
      cycleMinutes: r.cycleMinutes,
      inputs: r.inputs ?? {},
      outputs: r.outputs ?? {},
      requiresWater: r.requiresWater ?? 0,
    })),
    workerSlots: op.workerSlots ?? 0,
    maintenancePerDay: op.maintenancePerDay ?? {},
    storageCapacity: op.storageCapacity ?? 0,
    bufferCapacity: op.bufferCapacity ?? 0,
    /* 耗电即视为「需供电才能生产」。不复用 utility.productionRequiresPower ——
       那是 G010 流体域的开关，两个域抢同一个字段会让内容作者无法分别表达。 */
    requiresPower: (raw.utility?.powerDrawWatts ?? 0) > 0,
  };
}
const getProductionSpec = (id: string): ProductionSpec | undefined => PRODUCTION_SPECS[id];

/** 建筑定义 -> 岗位投影（G012）。 */
const JOB_SPECS: Record<string, JobSpec> = {};
for (const raw of Object.values(DEF_MODULES)) {
  const op = raw.operation;
  if (!op) continue;
  JOB_SPECS[raw.id] = {
    id: raw.id,
    workerSlots: op.workerSlots ?? 0,
    requiredSkills: (op.requiredSkills ?? {}) as Readonly<Partial<Record<SkillId, number>>>,
  };
}
const getJobSpec = (id: string): JobSpec | undefined => JOB_SPECS[id];
export function fluidOf(type: string): FluidSpec | undefined { return FLUID_SPECS[type]; }
/** 建筑的电力属性（UI 判断该不该显示供电行）。 */
export function utilityOf(type: string): PowerSpec | undefined { return POWER_SPECS[type]; }
export function defOf(type: string): PlanBuildingSpec | undefined { return DEFS[type]; }
export function costOf(type: string): Record<string, number> { return DEFS[type]?.cost ?? {}; }

/* ============ 内核会话 ============ */

/**
 * 内核是 `let` 而不是 `const`。
 *
 * 原先是模块级常量，创建一次就固定 —— 「回主菜单再开一局」在物理上做不到，
 * 这正是「退出闭环」缺失的根源，不只是少个按钮。现在把装配抽成函数，
 * 新开一局或读档时整体替换实例；所有导出的投影函数都通过这个 let 取当前内核，
 * 因此换实例后自动指向新的那个。
 */
let kernel: SimulationKernel;
/* seq / lastCode 是会话状态，必须与 kernel 同域声明并在其之前 ——
   newSession() 在模块顶层就会被调用，声明在后面会触发 TDZ（本项目第三次踩）。 */
let seq = 0;
let lastCode: string | null = null;

/** 开局库存 —— 与接线前 RES_STOCK 一致。 */
/** 开局殖民地人口。 */
const COLONY_POPULATION = 48;

const INITIAL_STOCK: Readonly<Record<string, number>> = {
  metal: 8742, rare: 260, concrete: 1400, cell: 96,
};

/** 把命令处理器与系统装到一个内核上。顺序即契约，见下方注释。 */
function assemble(k: SimulationKernel): void {
  k.addCommandHandler(createPlanBuildingHandler(getDef));
  k.addCommandHandler(createSeedBuildingHandler(getDef));
  k.addCommandHandler(createLayRoadHandler());
  k.addCommandHandler(createCancelBuildingHandler(getDef));
  k.addCommandHandler(createDeconstructBuildingHandler(getDef));
  k.addCommandHandler(createMarkDamagedHandler());
  k.addCommandHandler(createSpendResourcesHandler());
  k.addCommandHandler(createLayCableHandler());
  k.addCommandHandler(createRemoveCableHandler());
  k.addCommandHandler(createSeedCableHandler());
  k.addCommandHandler(createLayPipeHandler());
  k.addCommandHandler(createRemovePipeHandler());
  k.addCommandHandler(createSeedPipeHandler());
  k.addCommandHandler(createSetValveHandler());
  // requireRoads:false —— 保持接线前的玩法。强制修路是设计决定，不由接线顺手引入。
  k.addSystem(createLogisticsSystem({ batchPerTick: 400, requireRoads: false }));
  k.addSystem(createConstructionWorkSystem(getDef, { maxWorkersPerSite: 4 }));
  k.addSystem(createPowerSystem(getPowerSpec));
  // 顺序即契约：电力必须在流体之前 —— 同 Tick 内先算出谁被切断，
  // 流体系统才能据此决定产氧机是否停摆（G010 验收第 1 条）。
  k.addSystem(createFluidSystem(getFluidSpec));
  /* 人口必须排在生产【之前】：生产要读本 Tick 的在岗人数。 */
  k.addSystem(createPopulationSystem({ getSpec: getJobSpec }));
  /* 生产必须排在电力与流体【之后】：它要读同一 Tick 里「谁被限电」「哪张管网有水」
     的结论。排在前面会读到上一 Tick 的旧值，玩家会看到停机原因慢一拍。 */
  k.addSystem(createProductionSystem({ getSpec: getProductionSpec, utility: makeUtilityProbe(k) }));
  k.addCommandHandler(createAssignJobHandler(getJobSpec));
  k.addCommandHandler(createUnassignJobHandler());
  k.addCommandHandler(createSetShiftHandler());
}

/**
 * 生产系统的公用设施探针。
 *
 * 用闭包持内核引用而不是快照：`newSession()` / `loadSession()` 会换掉内核实例，
 * 而 assemble() 每次都会重新构造探针，因此这里的 `k` 始终指向当前局的内核。
 */
function makeUtilityProbe(k: SimulationKernel): UtilityProbe {
  return {
    powered(entityId) {
      const w = k.worldState;
      return powerStatusOf(w.utilityNetworks, w.spatial, w.entities, entityId).connected;
    },
    shed(entityId) {
      const w = k.worldState;
      return powerStatusOf(w.utilityNetworks, w.spatial, w.entities, entityId).shed;
    },
    hasWater(entityId) {
      const w = k.worldState;
      const st = fluidStatusOf(w.fluidNetworks, w.spatial, w.entities, entityId);
      if (!st.connected || !st.componentId) return false;
      const rep = w.fluidNetworks.reports.find((r) => r.id === st.componentId);
      return (rep?.stored['water_clean'] ?? 0) > 0;
    },
  };
}

/** 开一局全新的。返回前内核已装配完毕、库存已就位。 */
export function newSession(seed = 20240): void {
  kernel = SimulationKernel.create({
    scenarioId: 'crater-zero-shell', seed, mapConfig: MAP_CONFIG,
  });
  assemble(kernel);
  Object.assign(kernel.worldState.resources.stock, INITIAL_STOCK);
  kernel.worldState.construction.workforce = 64;
  /* G012：劳动力**由真实居民推导**，不再是场景常量。
     播种用【从同一世界种子派生的独立流】：既保证同种子同结果，又不消耗内核
     自身的随机序列 —— 后者会让人口数量的任何调整都改变全局演化。
     AGENTS.md 禁止 Math.random 决定模拟结果。 */
  const popRng = SeededRng.fromSeed((seed ^ 0x504f5000) >>> 0);
  seedPopulation(kernel.worldState.population, COLONY_POPULATION, () => popRng.nextFloat());
  // 初次自动分配，让开局就有人上岗；玩家手动调整会加锁，之后自动器不再覆盖
  autoAssign(kernel.worldState.population, kernel.worldState.entities, getJobSpec);
  seq = 0;
}

/** 从存档世界恢复一局。系统装配与新局完全相同 —— 顺序不同会改变演化。 */
export function loadSession(world: Parameters<typeof SimulationKernel.fromSnapshot>[0]): void {
  kernel = SimulationKernel.fromSnapshot(world);
  assemble(kernel);
  seq = 0;
}

newSession();

function submit(type: string, payload: unknown): void {
  seq += 1;
  const c: GameCommand = {
    commandId: `shell_${seq}` as CommandId,
    type,
    issuedAtTick: kernel.worldState.clock.tick,
    clientSequence: seq,
    payload,
  };
  kernel.submitCommand(c);
}

/** 排空命令队列并推进一个 Tick，返回本批最后一条命令的结果码。 */
export function flush(): string | null {
  const out = kernel.step();
  const r = out.commandResults[out.commandResults.length - 1];
  lastCode = r ? (r.accepted ? null : r.rejectionCode ?? 'UNKNOWN') : null;
  return lastCode;
}

/* ============ 网格投影（唯一来源 = 内核配置） ============ */
export function secOf(ring: number): number {
  return sectorsOfRing(MAP_CONFIG, 'floor', ring);
}
export function wrapSct(ring: number, sct: number): number {
  const n = secOf(ring);
  return ((sct % n) + n) % n;
}
export function cellKey(ring: number, sct: number): string {
  return `${ring}:${wrapSct(ring, sct)}`;
}
function idOf(ring: number, sct: number): BuildCellId {
  return { layer: 'floor', ring, sector: wrapSct(ring, sct) };
}
export function cellCenter(ring: number, sct: number): { x: number; z: number; a: number } {
  const p = cellToWorld(MAP_CONFIG, idOf(ring, sct));
  let a = Math.atan2(p.z, p.x);
  if (a < 0) a += Math.PI * 2;
  return { x: p.x, z: p.z, a };
}
export function worldToCell(x: number, z: number): { r: number; sct: number } {
  const rr = Math.hypot(x, z);
  const r = Math.max(0, Math.min(RING_COUNT - 1, Math.round((rr - RING_R0) / RING_STEP)));
  let a = Math.atan2(z, x);
  if (a < 0) a += Math.PI * 2;
  return { r, sct: Math.floor((a / (Math.PI * 2)) * secOf(r)) };
}
/** 单元格是否已被占用 —— 读内核 spatial，不再有本地 cellTaken 集合。 */
export function isCellTaken(ring: number, sct: number): boolean {
  const cell = getCell(kernel.worldState.spatial, idOf(ring, sct));
  return !cell || cell.occupiedBy !== null;
}

/* ============ 资源投影 ============ */
export function stock(): Record<string, number> {
  return kernel.worldState.resources.stock as Record<string, number>;
}
/** 缺料清单（干跑，供 UI 预判；命令仍会在 Tick 内重新校验）。 */
export function shortagesOf(type: string): string[] {
  const def = DEFS[type];
  if (!def) return [];
  return resourceShortages(stock(), def.cost);
}

/* ============ 命令 ============ */
export interface CmdResult { ok: boolean; code?: string; entityId?: string }

function lastEntityId(): string | undefined {
  const all = listBuildings(kernel.worldState.entities);
  return all.length ? all[all.length - 1]!.entityId : undefined;
}

/** 场景预置（既有殖民地）：直接 operational、不扣当期库存，但仍是命令。 */
export function seedBuilding(type: string, ring: number, sct: number, rot = 0): CmdResult {
  submit('seed_building', { defId: type, anchor: idOf(ring, sct), rotationStep: rot & 7 });
  const code = flush();
  return code ? { ok: false, code } : { ok: true, entityId: lastEntityId() };
}

/** 玩家放置：扣料、走施工。 */
export function planBuilding(type: string, ring: number, sct: number, rot = 0): CmdResult {
  submit('plan_building', { defId: type, anchor: idOf(ring, sct), rotationStep: rot & 7 });
  const code = flush();
  return code ? { ok: false, code } : { ok: true, entityId: lastEntityId() };
}

export function removeBuilding(entityId: string): CmdResult {
  const b = buildingOf(entityId);
  // planned / under_construction 走取消（按阶段退款），operational 走拆除
  const type = b && (b.lifecycle === 'planned' || b.lifecycle === 'under_construction')
    ? 'cancel_building' : 'deconstruct_building';
  submit(type, { entityId });
  const code = flush();
  return code ? { ok: false, code } : { ok: true };
}

/** 为尚未建模为实体的支出扣账（基建段件）—— 过渡入口，见内核侧说明。 */
export function spendResources(cost: Record<string, number>, reason: string): CmdResult {
  submit('spend_resources', { cost, reason });
  const code = flush();
  return code ? { ok: false, code } : { ok: true };
}

export function layRoad(ring: number, sct: number): CmdResult {
  submit('lay_road', { cell: idOf(ring, sct) });
  const code = flush();
  return code ? { ok: false, code } : { ok: true };
}
/* ============ 电力 ============ */
function powerState(): PowerState {
  return kernel.worldState.utilityNetworks as PowerState;
}
export function layCable(ring: number, sct: number): CmdResult {
  submit('lay_cable', { cell: idOf(ring, sct) });
  const code = flush();
  return code ? { ok: false, code } : { ok: true };
}
export function removeCable(ring: number, sct: number): CmdResult {
  submit('remove_cable', { cell: idOf(ring, sct) });
  const code = flush();
  return code ? { ok: false, code } : { ok: true };
}
/** 场景预置电缆（既有殖民地电网，不扣当期库存）。 */
export function seedCable(ring: number, sct: number): void {
  submit('seed_cable', { cell: idOf(ring, sct) });
}
export function hasCable(ring: number, sct: number): boolean {
  return powerState().cableCells[simCellKey(idOf(ring, sct))] === true;
}
export function cableCellKeys(): string[] {
  return Object.keys(powerState().cableCells);
}
export function powerSummary(): ReturnType<typeof powerSummaryOf> {
  return powerSummaryOf(powerState());
}
/** 单栋建筑的供电状态；未接入电网返回 connected:false。 */
export function powerOf(entityId: string): { connected: boolean; componentId: string | null; shed: boolean } {
  return powerStatusOf(powerState(), kernel.worldState.spatial, kernel.worldState.entities, entityId);
}
/** 电网分量（覆盖层用）。 */
export function powerComponents(): Array<{ id: string; conduitKeys: readonly string[]; entityIds: readonly string[] }> {
  return powerTopology(kernel.worldState.spatial, powerState(), kernel.worldState.entities)
    .components.map((c) => ({ id: c.id, conduitKeys: c.conduitKeys, entityIds: c.entityIds }));
}

/* ============ 流体（氧气与水） ============ */
function fluidState(): FluidState {
  return kernel.worldState.fluidNetworks as FluidState;
}
export function layPipe(ring: number, sct: number): CmdResult {
  submit('lay_pipe', { cell: idOf(ring, sct) });
  const code = flush();
  return code ? { ok: false, code } : { ok: true };
}
export function removePipe(ring: number, sct: number): CmdResult {
  submit('remove_pipe', { cell: idOf(ring, sct) });
  const code = flush();
  return code ? { ok: false, code } : { ok: true };
}
export function seedPipe(ring: number, sct: number): void {
  submit('seed_pipe', { cell: idOf(ring, sct) });
}
export function setValve(ring: number, sct: number, closed: boolean): CmdResult {
  submit('set_valve', { cell: idOf(ring, sct), closed });
  const code = flush();
  return code ? { ok: false, code } : { ok: true };
}
export function hasPipe(ring: number, sct: number): boolean {
  return fluidState().pipeCells[simCellKey(idOf(ring, sct))] === true;
}
export function valveClosed(ring: number, sct: number): boolean {
  return fluidState().closedValves[simCellKey(idOf(ring, sct))] === true;
}
export function fluidSummary(): ReturnType<typeof fluidSummaryOf> {
  return fluidSummaryOf(fluidState());
}
/** 某种流体在全网最紧张的储备小时数（-1 = 全部充足）。由模拟层报表汇总，
 *  UI 不自己拿储量除 —— 验收要求 UI 与模拟数值同源。 */
export function fluidReserve(kind: 'oxygen' | 'water_raw' | 'water_clean'): number {
  let worst = -1;
  for (const r of fluidState().reports) {
    const h = r.reserveHours[kind];
    if (h >= 0 && (worst < 0 || h < worst)) worst = h;
  }
  return worst;
}

export function fluidStatus(entityId: string): { connected: boolean; componentId: string | null; contamination: number } {
  return fluidStatusOf(fluidState(), kernel.worldState.spatial, kernel.worldState.entities, entityId);
}

export function hasRoad(ring: number, sct: number): boolean {
  return kernel.worldState.construction.roadCells[simCellKey(idOf(ring, sct))] === true;
}

/* ============ 建筑投影 ============ */
export function buildingOf(entityId: string): BuildingComponent | undefined {
  return listBuildings(kernel.worldState.entities).find((b) => b.entityId === entityId)?.building;
}
export function allBuildings(): Array<{ entityId: string; building: BuildingComponent }> {
  return listBuildings(kernel.worldState.entities);
}
/** 工地进度投影：缺口 / 剩余工时 / 阻塞原因（供详情面板）。 */
export function outlookOf(entityId: string): ReturnType<typeof constructionOutlook> | null {
  const b = buildingOf(entityId);
  return b ? constructionOutlook(b) : null;
}

/* ============ 时钟 ============ */
/** 时钟投影。内核 1 Tick = 1 游戏分钟；渲染层不再自增任何时间量。 */
export function clock(): { tick: number; minuteOfDay: number; day: number } {
  const c = kernel.worldState.clock;
  return { tick: c.tick, minuteOfDay: c.minuteOfDay, day: c.day };
}
/** 推进 n 个 Tick（= n 游戏分钟），顺带排空命令队列。 */
export function advance(ticks: number): void {
  for (let i = 0; i < ticks; i++) kernel.step();
}
/** 把时钟对齐到指定的开局时刻（仅在场景初始化时调用一次）。 */
export function setClock(minuteOfDay: number, day: number): void {
  kernel.worldState.clock.minuteOfDay = minuteOfDay;
  kernel.worldState.clock.day = day;
}

/* ============ 存档（G014） ============ */

/** 内容包版本：内容变了要能被识别出来。取自构建期注入或回退常量。 */
const CONTENT_VERSION = 'g010';

/** 摄像机元数据的对外别名（外壳用，避免它直接 import simulation-core）。 */
export type ViewMetaLike = ViewMeta;

/**
 * 打包当前世界为存档信封。
 * @param nowMs 真实时间戳由调用方注入 —— 模拟层内部禁止 Date.now()
 */
export function makeSave(nowMs: number, view?: ViewMeta | null): SaveEnvelope {
  return buildEnvelope({
    world: kernel.exportSnapshot(),
    contentVersion: CONTENT_VERSION,
    createdAtMs: nowMs,
    view: view ?? null,
    buildings: listBuildings(kernel.worldState.entities).length,
  });
}

/** 读入一份存档：校验 → 恢复会话。失败返回稳定拒绝码，绝不半途切换内核。 */
export function applySave(raw: unknown): { ok: boolean; code?: string; view?: ViewMeta | null } {
  const parsed = parseEnvelope(raw, CONTENT_VERSION);
  if (!parsed.ok || !parsed.envelope) return { ok: false, code: parsed.rejection ?? 'UNKNOWN' };
  loadSession(parsed.envelope.world);
  return { ok: true, view: parsed.envelope.view };
}

/* ============ 确定性（供 e2e） ============ */
export function snapshot(): unknown { return kernel.exportSnapshot(); }
export function stateHash(): string { return kernel.stateHash(); }
export { MAP_CONFIG };

/** 从快照恢复并校验：位置、朝向、资源、时钟是否一致（G008A 验收用）。 */
export function verifySnapshotRoundTrip(): {
  ok: boolean; hashMatch: boolean; buildings: number; mismatches: string[];
} {
  const snap = kernel.exportSnapshot() as Parameters<typeof SimulationKernel.fromSnapshot>[0];
  const k2 = SimulationKernel.fromSnapshot(snap);
  const a = listBuildings(kernel.worldState.entities);
  const b = listBuildings(k2.worldState.entities);
  const mismatches: string[] = [];
  if (a.length !== b.length) mismatches.push(`建筑数 ${a.length} vs ${b.length}`);
  for (let i = 0; i < Math.min(a.length, b.length); i++) {
    const x = a[i]!, y = b[i]!;
    if (x.entityId !== y.entityId) mismatches.push(`#${i} 实体 ID`);
    if (simCellKey(x.building.anchor) !== simCellKey(y.building.anchor)) mismatches.push(`#${i} 位置`);
    if (x.building.rotationStep !== y.building.rotationStep) mismatches.push(`#${i} 朝向`);
    if (x.building.lifecycle !== y.building.lifecycle) mismatches.push(`#${i} 生命周期`);
  }
  for (const [res, v] of Object.entries(stock())) {
    const w = (k2.worldState.resources.stock as Record<string, number>)[res];
    if (v !== w) mismatches.push(`资源 ${res} ${v} vs ${w}`);
  }
  const c1 = kernel.worldState.clock, c2 = k2.worldState.clock;
  if (c1.tick !== c2.tick || c1.minuteOfDay !== c2.minuteOfDay || c1.day !== c2.day) {
    mismatches.push('时钟');
  }
  const hashMatch = k2.stateHash() === kernel.stateHash();
  return { ok: hashMatch && mismatches.length === 0, hashMatch, buildings: a.length, mismatches };
}

declare global {
  interface Window {
    /** 模拟侧只读探针。main.ts 的 __cz 是渲染句柄，两者不可同名。 */
    __czSim?: {
      stock: () => Record<string, number>;
      buildings: () => number;
      hash: () => string;
      clock: () => { tick: number; minuteOfDay: number; day: number };
      roads: () => number;
      verify: () => ReturnType<typeof verifySnapshotRoundTrip>;
      lifecycles: () => Record<string, number>;
      freeCells: (ring: number) => Array<{ ring: number; sct: number; x: number; z: number }>;
      cables: () => number;
      power: () => ReturnType<typeof powerSummaryOf>;
      components: () => number;
      pipes: () => number;
      valves: () => number;
      fluid: () => ReturnType<typeof fluidSummaryOf>;
      /* G011 生产链 */
      economy: () => ReturnType<typeof economySummary>;
      flow: (resourceId: string) => ReturnType<typeof resourceFlow>;
      production: (entityId: string) => ReturnType<typeof productionStatusOf>;
      workforce: () => { available: number; assigned: number };
      /* G012 人口与劳动力 */
      populationSummary: () => ReturnType<typeof populationSummary>;
      staffedCount: () => Record<string, number>;
      citizens: () => ReturnType<typeof citizenList>;
      jobsOf: (entityId: string) => ReturnType<typeof jobPanel>;
      assign: (citizenId: string, buildingId: string, slot?: number) => CmdResult;
      unassign: (citizenId: string) => CmdResult;
      setShift: (citizenId: string, shift: ShiftId) => CmdResult;
      rebalance: () => void;
      makeSave: (nowMs: number) => SaveEnvelope;
      applySave: (raw: unknown) => { ok: boolean; code?: string };
      newSession: (seed?: number) => void;
    };
  }
}
window.__czSim = {
  stock: () => ({ ...stock() }),
  buildings: () => listBuildings(kernel.worldState.entities).length,
  hash: () => kernel.stateHash(),
  clock,
  roads: () => Object.keys(kernel.worldState.construction.roadCells).length,
  cables: () => cableCellKeys().length,
  power: () => powerSummary(),
  components: () => powerComponents().length,
  pipes: () => Object.keys(fluidState().pipeCells).length,
  valves: () => Object.keys(fluidState().closedValves).length,
  fluid: () => fluidSummary(),
  economy: () => economy(),
  flow: (resourceId: string) => resourceFlow(resourceId),
  production: (entityId: string) => productionOf(entityId),
  workforce: () => ({
    available: kernel.worldState.production.workforceAvailable,
    assigned: kernel.worldState.production.workforceAssigned,
  }),
  populationSummary: () => population(),
  staffedCount: () => ({ ...kernel.worldState.population.staffedCount }),
  citizens: () => citizens(),
  jobsOf: (entityId: string) => jobsOf(entityId),
  assign: (citizenId: string, buildingId: string, slot?: number) =>
    assignJob(citizenId, buildingId, slot),
  unassign: (citizenId: string) => unassignJob(citizenId),
  setShift: (citizenId: string, shift: ShiftId) => setShift(citizenId, shift),
  rebalance: () => { rebalanceJobs(); },
  makeSave: (nowMs: number) => makeSave(nowMs),
  applySave: (raw: unknown) => applySave(raw),
  newSession: (seed?: number) => { newSession(seed); },
  verify: verifySnapshotRoundTrip,
  /** 该环全部空闲单元格（e2e 与手工验证用：demo 城很密，得先找到空位）。 */
  freeCells: (ring: number) => {
    const out: Array<{ ring: number; sct: number; x: number; z: number }> = [];
    for (let sct = 0; sct < secOf(ring); sct++) {
      if (!isCellTaken(ring, sct)) {
        const c = cellCenter(ring, sct);
        out.push({ ring, sct, x: c.x, z: c.z });
      }
    }
    return out;
  },
  lifecycles: () => {
    const out: Record<string, number> = {};
    for (const { building } of listBuildings(kernel.worldState.entities)) {
      out[building.lifecycle] = (out[building.lifecycle] ?? 0) + 1;
    }
    return out;
  },
};

/* ===================== G011 生产链投影 ===================== */

/** 某栋建筑的产线状况（停机原因、用工、缓冲）。 */
export function productionOf(entityId: string): ReturnType<typeof productionStatusOf> {
  return productionStatusOf(kernel.worldState.production, entityId);
}

/** 殖民地经济概览：容量、用工、每种资源的库存与日流量。HUD 直接投影这个。 */
export function economy(): ReturnType<typeof economySummary> {
  return economySummary(kernel.worldState.production, kernel.worldState.resources.stock);
}

/** 某种资源的来源/消耗分解（按建筑定义归类）。 */
export function resourceFlow(resourceId: string): {
  producedPerDay: number; consumedPerDay: number;
  byProducer: Record<string, number>; byConsumer: Record<string, number>;
} {
  const f = kernel.worldState.production.flows[resourceId];
  return {
    producedPerDay: f?.producedPerDay ?? 0,
    consumedPerDay: f?.consumedPerDay ?? 0,
    byProducer: { ...(f?.byProducer ?? {}) },
    byConsumer: { ...(f?.byConsumer ?? {}) },
  };
}

/** 生产建筑的定义投影（UI 判断该不该显示产线行）。 */
export function productionSpecOf(defId: string): ProductionSpec | undefined {
  return PRODUCTION_SPECS[defId];
}

/** 停机原因的稳定码 -> 中文文案。UI 只做映射，不自造原因。 */
export const PRODUCTION_BLOCK_TEXT: Readonly<Record<ProductionBlock, string>> = {
  NO_POWER: '未接入电网',
  POWER_SHED: '限电中',
  NO_WATER: '缺水',
  NO_WORKERS: '人手不足',
  INPUT_MISSING: '原料不足',
  OUTPUT_FULL: '仓储已满',
};

/* ===================== G012 人口与劳动力投影 ===================== */

/** 人口列表（UI 用）。与岗位面板互相定位。 */
export function citizens(): ReturnType<typeof citizenList> {
  return citizenList(kernel.worldState.population, kernel.worldState.clock.minuteOfDay);
}

/** 某栋楼的岗位面板；槽位数取自建筑定义。 */
export function jobsOf(entityId: string): ReturnType<typeof jobPanel> {
  const b = kernel.worldState.entities.components['building']?.[entityId] as
    { defId: string } | undefined;
  const slots = b ? (JOB_SPECS[b.defId]?.workerSlots ?? 0) : 0;
  return jobPanel(kernel.worldState.population, entityId, slots, kernel.worldState.clock.minuteOfDay);
}

/** 殖民地人口概览 —— 顶栏「人口」那格的真实来源。 */
export function population(): ReturnType<typeof populationSummary> {
  return populationSummary(kernel.worldState.population, kernel.worldState.clock.minuteOfDay);
}

/** 某栋楼的在册员工 id。 */
export function staffIdsOf(entityId: string): string[] {
  return staffOf(kernel.worldState.population, entityId);
}

/** 某名居民的岗位。 */
export function jobOfCitizen(citizenId: string): ReturnType<typeof jobOf> {
  return jobOf(kernel.worldState.population, citizenId);
}

export function assignJob(citizenId: string, buildingId: string, slot?: number): CmdResult {
  submit('assign_job', slot === undefined
    ? { citizenId, buildingId } : { citizenId, buildingId, slot });
  const code = flush();
  return code ? { ok: false, code } : { ok: true };
}
export function unassignJob(citizenId: string): CmdResult {
  submit('unassign_job', { citizenId });
  const code = flush();
  return code ? { ok: false, code } : { ok: true };
}
export function setShift(citizenId: string, shift: ShiftId): CmdResult {
  submit('set_shift', { citizenId, shift });
  const code = flush();
  return code ? { ok: false, code } : { ok: true };
}

/** 重跑自动分配（不覆盖锁定岗位）。 */
export function rebalanceJobs(): void {
  autoAssign(kernel.worldState.population, kernel.worldState.entities, getJobSpec);
}

/** 人口相关拒绝码 -> 中文文案。UI 只做映射，不自造原因。 */
export const JOB_REJECT_TEXT: Readonly<Record<string, string>> = {
  CITIZEN_NOT_FOUND: '找不到该居民',
  CITIZEN_CANNOT_WORK: '该居民无法工作',
  BUILDING_NOT_FOUND: '找不到该建筑',
  BUILDING_NOT_OPERATIONAL: '建筑尚未投入运转',
  JOB_SLOT_INVALID: '岗位编号无效',
  JOB_SLOT_TAKEN: '该岗位已被占用',
  JOB_NO_SLOTS: '该建筑没有岗位',
  ALREADY_ASSIGNED_HERE: '该居民已在此岗位',
  NOT_ASSIGNED: '该居民当前没有岗位',
  SHIFT_INVALID: '班次无效',
};
